import { useState } from 'react';
import { Box, styled } from '@material-ui/core';
import { SText, SInput } from '@setvi/shared/components';
import { PillTabs } from 'pages/auto-rfq/pages/list/components/create-dialog/rfq-info-content/styled';
import { WarningIcon } from '@setvi/shared/components/sicons';
import { Photo } from '../components';

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '360px',
  overflow: 'hidden auto',
  padding: theme.spacing(1, 3)
}));

const SectionTitle = styled(SText)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: '#333D47',
  margin: theme.spacing(1, 0, 3)
}));

const IconWrapper = styled(SText)(({ theme }) => ({
  background: theme.palette.primary.main,
  borderRadius: 99,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24
}));

const InfoWrapper = styled(SText)({
  background: '#EBF5FF',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: 8,
  boxShadow: '0px 2px 4px 0px #30363C29'
});

export enum TabType {
  PROFILE = 'my-profile',
  OTHER = 'use-other'
}

export interface UserData {
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  photo: string;
}

interface PreparedByData {
  type: TabType;
  usedForOtherSalesRep?: boolean;
  otherTabCache?: UserData;
  profileTabCache?: UserData;
  companyName?: string;
  companyAddress?: string;
}

interface PreparedByStepProps {
  data: PreparedByData;
  onChange: (data: PreparedByData) => void;
  onImageUpload: (file: File) => void;
  emailError?: string;
}

export const PreparedByStep = ({
  data,
  onChange,
  onImageUpload,
  emailError
}: PreparedByStepProps) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const tabs = [
    {
      label: 'My Profile',
      value: TabType.PROFILE
    },
    {
      label: 'Use Other',
      value: TabType.OTHER
    }
  ];

  // Get current active data based on tab type
  const currentData =
    data.type === TabType.PROFILE ? data.profileTabCache : data.otherTabCache;

  const handleTabChange = (tab: TabType) => {
    // Simply update the type and flag
    // Parent component will handle showing the correct cached data
    onChange({
      ...data,
      type: tab,
      usedForOtherSalesRep: tab === TabType.OTHER
    });
  };

  const handleChange = (field: string, value: string) => {
    // Validate phone number to only allow digits, +, and -
    if (field === 'contactNumber') {
      const validPhonePattern = /^[\d+-]*$/;
      if (!validPhonePattern.test(value)) {
        return;
      }
    }

    // Validate email format - allow only valid email characters while typing
    if (field === 'email') {
      const validEmailPattern = /^[a-zA-Z0-9._%+-]*@?[a-zA-Z0-9.-]*$/;
      if (!validEmailPattern.test(value)) {
        return;
      }
    }

    // Update the appropriate cache based on current tab type
    if (data.type === TabType.PROFILE) {
      onChange({
        ...data,
        profileTabCache: {
          ...data.profileTabCache,
          [field]: value
        }
      });
    } else {
      onChange({
        ...data,
        otherTabCache: {
          ...data.otherTabCache,
          [field]: value
        }
      });
    }
  };

  const getDisplayName = () => {
    if (data.type === TabType.PROFILE) {
      return `${currentData?.firstName} ${currentData?.lastName}`.trim();
    }
    return currentData?.fullName;
  };

  return (
    <Container>
      <Box
        style={{
          marginLeft: -12
        }}>
        <PillTabs
          fullWidth
          tabs={tabs}
          currentTab={data.type}
          onTabChange={val => handleTabChange(val as TabType)}
        />
      </Box>

      {data.type === TabType.OTHER && (
        <InfoWrapper>
          <IconWrapper>
            <WarningIcon
              fill="white"
              style={{
                width: 14,
                height: 14,
                transform: 'rotate(180deg)'
              }}
            />
          </IconWrapper>
          <SText
            fontColor="#333D47"
            title="These details apply only to this quote and won't update your profile."
            maxLines={3}
          />
        </InfoWrapper>
      )}

      <SText size="lg" weight="bold">
        Prepared By
      </SText>

      <Box style={{ marginTop: 12, marginBottom: 10 }}>
        <Photo
          handleChange={(name, value, file) => {
            handleChange?.(name, value);
            onImageUpload?.(file || null);
          }}
          photo={currentData?.photo}
          alt={getDisplayName()}
        />
      </Box>

      <Box>
        <SInput
          newDesign
          fullWidth
          required
          label="First Name"
          placeholder="Enter first name"
          value={currentData?.firstName || ''}
          onChange={e => handleChange('firstName', e.target.value)}
          onBlur={() => handleBlur('firstName')}
          error={touched.firstName && !currentData?.firstName}
        />
        <SInput
          newDesign
          fullWidth
          required
          label="Last Name"
          placeholder="Enter last name"
          value={currentData?.lastName || ''}
          onChange={e => handleChange('lastName', e.target.value)}
          onBlur={() => handleBlur('lastName')}
          error={touched.lastName && !currentData?.lastName}
        />

        <SInput
          newDesign
          fullWidth
          required
          label="Phone Number"
          value={currentData?.contactNumber || ''}
          placeholder="Enter phone number"
          onChange={e => handleChange('contactNumber', e.target.value)}
          onBlur={() => handleBlur('contactNumber')}
          error={touched.contactNumber && !currentData?.contactNumber}
        />

        <SInput
          newDesign
          fullWidth
          required
          label="Email Address"
          value={currentData?.email || ''}
          disabled={data.type === TabType.PROFILE}
          placeholder="Enter email address"
          onChange={e => handleChange('email', e.target.value)}
          onBlur={() => handleBlur('email')}
          error={
            touched.email ? !!emailError || !currentData?.email : !!emailError
          }
          helperText={emailError}
        />

        <SectionTitle>Company information</SectionTitle>

        <SInput
          newDesign
          label="Company Name"
          value={data.companyName || ''}
          placeholder="Enter company name"
          disabled
          fullWidth
        />

        <SInput
          newDesign
          label="Address"
          value={data.companyAddress || ''}
          placeholder="Enter address"
          disabled
          fullWidth
        />
      </Box>
    </Container>
  );
};
